import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { Users, Trophy, Copy, Gift, CheckCircle, Clock, RefreshCw } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const StudentReferrals = () => {
  const { token, user } = useAuth();
  const [referrals, setReferrals] = useState([]);
  const [stats, setStats] = useState({ total_referrals: 0, successful_referrals: 0, pending_referrals: 0 });
  const [loading, setLoading] = useState(true);
  const [referralEmail, setReferralEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [resendingId, setResendingId] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const [referralsRes, statsRes] = await Promise.all([
          axios.get(`${API_URL}/api/referrals/my`, { headers }),
          axios.get(`${API_URL}/api/referrals/stats`, { headers })
        ]);
        setReferrals(referralsRes.data);
        setStats(statsRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const copyReferralCode = () => {
    navigator.clipboard.writeText(user?.referral_code || '');
    toast.success('Referral code copied to clipboard!');
  };

  const sendReferral = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await axios.post(`${API_URL}/api/referrals`, {
        referrer_id: user.id,
        referred_email: referralEmail
      }, { headers: { Authorization: `Bearer ${token}` } });

      // Optimistically add the new referral to the top of the list
      const newReferral = {
        id: res.data.id,
        referred_email: referralEmail,
        status: 'pending',
        created_at: new Date().toISOString(),
      };
      setReferrals(prev => [newReferral, ...prev]);
      setStats(prev => ({
        ...prev,
        total_referrals: prev.total_referrals + 1,
        pending_referrals: prev.pending_referrals + 1,
      }));

      toast.success('Referral invitation sent!');
      setReferralEmail('');
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || 'Failed to send referral';
      console.error('[Referral] API error:', error.response?.status, msg, error);
      toast.error(msg);
    } finally {
      setSending(false);
    }
  };

  const resendInvite = async (referral) => {
    setResendingId(referral.id);
    try {
      await axios.post(
        `${API_URL}/api/referrals/${referral.id}/resend`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Invite resent to ${referral.referred_email}`);
    } catch (error) {
      const msg = error.response?.data?.detail || error.message || 'Failed to resend invite';
      console.error('[Referral] Resend error:', error.response?.status, msg, error);
      toast.error(msg);
    } finally {
      setResendingId(null);
    }
  };

  return (
    <DashboardLayout title="Referral Program">
      {/* Referral Code Card */}
      <Card className="mb-6 bg-gradient-to-r from-secondary/20 to-secondary/5 border-secondary/30" data-testid="referral-code-card">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="w-16 h-16 rounded-2xl bg-secondary flex items-center justify-center">
              <Trophy className="w-8 h-8 text-secondary-foreground" />
            </div>
            <div className="flex-1 text-center md:text-left">
              <h3 className="font-heading text-xl font-bold mb-1">Your Referral Code</h3>
              <p className="text-muted-foreground text-sm mb-3">Share this code with friends and earn rewards!</p>
              <div className="flex items-center gap-2 justify-center md:justify-start">
                <code className="bg-white px-4 py-2 rounded-lg font-mono text-xl font-bold">
                  {user?.referral_code || 'KOTA1234'}
                </code>
                <Button size="icon" variant="secondary" onClick={copyReferralCode} data-testid="copy-code-btn">
                  <Copy className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4 mb-6">
        <Card>
          <CardContent className="p-4 text-center">
            <Users className="w-8 h-8 mx-auto text-primary mb-2" />
            <p className="text-2xl font-bold">{stats.total_referrals}</p>
            <p className="text-sm text-muted-foreground">Total Referrals</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <CheckCircle className="w-8 h-8 mx-auto text-green-500 mb-2" />
            <p className="text-2xl font-bold">{stats.successful_referrals}</p>
            <p className="text-sm text-muted-foreground">Successful</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <Clock className="w-8 h-8 mx-auto text-yellow-500 mb-2" />
            <p className="text-2xl font-bold">{stats.pending_referrals}</p>
            <p className="text-sm text-muted-foreground">Pending</p>
          </CardContent>
        </Card>
      </div>

      {/* Send Referral */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-lg">Invite a Friend</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={sendReferral} className="flex gap-2">
            <Input
              type="email"
              placeholder="Friend's email address"
              value={referralEmail}
              onChange={(e) => setReferralEmail(e.target.value)}
              required
              data-testid="referral-email-input"
            />
            <Button type="submit" disabled={sending} data-testid="send-referral-btn">
              {sending ? 'Sending...' : 'Send Invite'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Referral History */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Referral History</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-16 bg-slate-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : referrals.length > 0 ? (
            <div className="space-y-4">
              {referrals.map((referral) => (
                <div
                  key={referral.id}
                  className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-xl shadow-sm hover:shadow-md hover:border-slate-300 transition-all"
                  data-testid={`referral-${referral.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${
                      referral.status === 'completed' ? 'bg-green-100' : 'bg-yellow-100'
                    }`}>
                      {referral.status === 'completed'
                        ? <CheckCircle className="w-4 h-4 text-green-600" />
                        : <Clock className="w-4 h-4 text-yellow-600" />
                      }
                    </div>
                    <div className="min-w-0">
                      <p className="font-medium truncate">{referral.referred_email}</p>
                      <p className="text-sm text-muted-foreground">
                        {referral.created_at?.split('T')[0]}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 shrink-0">
                    <Badge className={
                      referral.status === 'completed'
                        ? 'bg-green-100 text-green-700 border-0'
                        : 'bg-yellow-100 text-yellow-700 border-0'
                    }>
                      {referral.status}
                    </Badge>
                    {referral.status === 'pending' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => resendInvite(referral)}
                        disabled={resendingId === referral.id}
                        className="text-muted-foreground hover:text-primary h-8 px-2"
                        data-testid={`resend-btn-${referral.id}`}
                      >
                        <RefreshCw className={`w-3.5 h-3.5 mr-1 ${resendingId === referral.id ? 'animate-spin' : ''}`} />
                        {resendingId === referral.id ? 'Sending...' : 'Resend'}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center">
              <Gift className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
              <p className="text-muted-foreground">No referrals yet. Start inviting friends!</p>
            </div>
          )}
        </CardContent>
      </Card>
    </DashboardLayout>
  );
};

export default StudentReferrals;
